import { Sparkles, Target, Thermometer, Gauge, Timer, Layers, Shield, Maximize2 } from "lucide-react";

const CLINIC_IMG = "/images/cami_p5_img1.jpeg";

const coreFeatures = [
  {
    icon: Sparkles,
    title: "Gold-Plated Electrodes",
    desc: "24K gold-plated microneedle electrodes ensure optimal RF energy conduction and biocompatibility.",
  },
  {
    icon: Target,
    title: "Dual RF Frequencies",
    desc: "2.2MHz for non-insulated and 700KHz for insulated — each optimized for different tissue depths.",
  },
  {
    icon: Thermometer,
    title: "Selective Dermal Heating",
    desc: "Electrical impedance differences between epidermis and dermis ensure targeted coagulation zones.",
  },
  {
    icon: Gauge,
    title: "Precision Motor Control",
    desc: "Electronically controlled smooth-motion motor for consistent, repeatable needle insertion.",
  },
  {
    icon: Timer,
    title: "Adjustable Pulse Duration",
    desc: "Up to 200ms pulse duration with 10–25W power range for fine-tuned energy delivery.",
  },
  {
    icon: Layers,
    title: "Multi-Layer Stratification",
    desc: "IS and IC series support 1–5 depth layers, targeting superficial and deep skin structures simultaneously.",
  },
  {
    icon: Shield,
    title: "Epidermal Protection",
    desc: "Insulated needle tips protect the epidermis from thermal damage — safe for Fitzpatrick types I–VI.",
  },
  {
    icon: Maximize2,
    title: "Vacuum Enhancement",
    desc: "29mmHg negative pressure adsorption ensures uniform skin contact and even depth penetration.",
  },
];

export default function FeaturesBreakdown() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-24 lg:py-32 px-6 lg:px-12"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="reveal mb-20">
          <p
            className="text-xs tracking-[0.3em] uppercase mb-4"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
          >
            Engineering Excellence
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Built Without<br />
            <span className="text-white/40">Compromise.</span>
          </h2>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16 reveal">
          {coreFeatures.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="bento-card bento-hr p-6"
                data-testid={`feature-card-${i}`}
              >
                <Icon size={18} className="mb-5" style={{ color: "#D4AF37" }} />
                <h4
                  className="text-lg font-light text-white mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {feature.title}
                </h4>
                <p className="text-xs text-white/40 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {feature.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Clinic Image Banner */}
        <div className="reveal relative overflow-hidden h-64 lg:h-80">
          <img
            src={CLINIC_IMG}
            alt="Luxury clinic interior"
            className="w-full h-full object-cover"
            data-testid="clinic-image"
          />
          <div className="absolute inset-0 bg-[#0A0A0C]/60" />
          <div className="absolute inset-0 flex items-center justify-center text-center px-6">
            <div>
              <p
                className="text-xs tracking-[0.3em] uppercase mb-4"
                style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
              >
                One Device. Every Treatment.
              </p>
              <h3
                className="text-2xl sm:text-3xl lg:text-4xl font-light text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Designed for the Modern Aesthetic Practice
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
